import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  KeyRound,
  Link2,
  MoreHorizontal,
  Pause,
  Phone,
  Play,
  Plus,
  Tags,
  Timer,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  customersApi,
  type BillingPreview,
  type Customer,
  type CustomerPlan,
} from "@/features/customers/api";
import { planEconomics } from "@/features/customers/economics";
import { money, onDate, perMinute } from "@/features/customers/format";
import { MinutesCell } from "@/features/customers/minutes";
import {
  AddMinutesDialog,
  AssignDialog,
  CUSTOMERS_KEY,
  DeleteDialog,
  ResetPasswordDialog,
  errText,
} from "@/features/customers/dialogs";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

const KEY = CUSTOMERS_KEY;

export default function CustomersPage() {
  const me = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...KEY], queryFn: customersApi.list, staleTime: 30_000 });

  const [addOpen, setAddOpen] = useState(false);
  const [assignFor, setAssignFor] = useState<Customer | null>(null);
  const [pwFor, setPwFor] = useState<Customer | null>(null);
  const [minutesFor, setMinutesFor] = useState<Customer | null>(null);
  const [deleteFor, setDeleteFor] = useState<Customer | null>(null);

  if (!hasMinRole(me?.role, "admin")) return <Navigate to="/dashboard" replace />;

  const customers = q.data ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: [...KEY] });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground">
            Your own customers — each logs into this same dashboard and sees only
            their own agents, numbers, calls and plan.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/customer-plans">
              <Tags className="mr-2 h-4 w-4" /> Plans
            </Link>
          </Button>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add customer
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {q.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : customers.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">
              No customers yet. Add your first customer to give them their own
              login, plan and space in this dashboard.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Customer</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Assistants</th>
                  <th className="px-4 py-3 font-medium">Numbers</th>
                  <th className="px-4 py-3 font-medium">Minutes this period</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <Link
                        to={`/customers/${c.id}`}
                        className="font-medium hover:underline"
                      >
                        {c.name}
                      </Link>
                      <div className="text-xs text-muted-foreground">{c.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      {c.plan ? (
                        <>
                          <div>{c.plan.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {money(c.plan.price_cents)}/mo ·{" "}
                            {c.plan.included_minutes.toLocaleString()} min
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs font-medium",
                          c.status === "active"
                            ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400"
                            : "bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400",
                        )}
                      >
                        {c.status === "active" ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1">
                        <Bot className="h-3.5 w-3.5 text-muted-foreground" />
                        {c.assistant_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                        {c.phone_number_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <MinutesCell usage={c.usage} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <RowMenu
                        customer={c}
                        onAssign={() => setAssignFor(c)}
                        onPassword={() => setPwFor(c)}
                        onMinutes={() => setMinutesFor(c)}
                        onDelete={() => setDeleteFor(c)}
                        onChanged={invalidate}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <AddCustomerDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={invalidate} />
      {assignFor && (
        <AssignDialog customer={assignFor} onClose={() => setAssignFor(null)} onChanged={invalidate} />
      )}
      {pwFor && <ResetPasswordDialog customer={pwFor} onClose={() => setPwFor(null)} />}
      {minutesFor && (
        <AddMinutesDialog
          customer={minutesFor}
          onClose={() => setMinutesFor(null)}
          onChanged={invalidate}
        />
      )}
      {deleteFor && (
        <DeleteDialog customer={deleteFor} onClose={() => setDeleteFor(null)} onDeleted={invalidate} />
      )}
    </div>
  );
}

function RowMenu({
  customer,
  onAssign,
  onPassword,
  onMinutes,
  onDelete,
  onChanged,
}: {
  customer: Customer;
  onAssign: () => void;
  onPassword: () => void;
  onMinutes: () => void;
  onDelete: () => void;
  onChanged: () => void;
}) {
  const toggle = useMutation({
    mutationFn: () =>
      customersApi.update(customer.id, {
        status: customer.status === "active" ? "paused" : "active",
      }),
    onSuccess: () => {
      toast.success(customer.status === "active" ? "Customer paused." : "Customer activated.");
      onChanged();
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onAssign}>
          <Link2 className="mr-2 h-4 w-4" /> Assign assistants &amp; numbers
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onMinutes}>
          <Timer className="mr-2 h-4 w-4" /> Add minutes
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => toggle.mutate()}>
          {customer.status === "active" ? (
            <>
              <Pause className="mr-2 h-4 w-4" /> Pause
            </>
          ) : (
            <>
              <Play className="mr-2 h-4 w-4" /> Activate
            </>
          )}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onPassword}>
          <KeyRound className="mr-2 h-4 w-4" /> Reset password
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CostRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{children}</span>
    </div>
  );
}

/** What this customer will cost, before the tenant commits to creating them. */
function CostPanel({ p }: { p: BillingPreview }) {
  if (!p.configured) {
    return (
      <div className="rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
        No per-customer fee is set on your plan — adding customers is free.
        Calls are still charged at {perMinute(p.per_minute_cents)} per minute
        from your credit balance.
      </div>
    );
  }

  if (!p.affordable) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm">
        <p className="font-medium text-destructive">
          Not enough credit to add a customer
        </p>
        <p className="mt-1 text-muted-foreground">
          This customer costs {money(p.fee_cents)} for the first month, charged
          now, and your balance is {money(p.balance_cents)}.{" "}
          <Link to="/billing" className="font-medium underline underline-offset-2">
            Top up your balance
          </Link>{" "}
          to continue.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2.5 rounded-lg border bg-muted/40 p-3 text-sm">
      <div className="flex items-baseline justify-between gap-4">
        <span className="font-medium">{money(p.fee_cents)} charged now</span>
        <span className="text-xs text-muted-foreground">
          first month, in advance
        </span>
      </div>
      <div className="space-y-1.5 border-t pt-2.5">
        <CostRow label="Your balance">
          <span className="text-muted-foreground line-through">
            {money(p.balance_cents)}
          </span>{" "}
          {money(p.balance_after_cents)}
        </CostRow>
        <CostRow label="Then every month from">
          {onDate(p.next_charge_on)}
        </CostRow>
        <CostRow label="Calls">
          {perMinute(p.per_minute_cents)} / min, same balance
        </CostRow>
        <CostRow label="After adding this one">
          {p.active_customers + 1} customers ·{" "}
          {money(p.monthly_total_after_cents)} / month
        </CostRow>
      </div>
      <p className="border-t pt-2.5 text-xs text-muted-foreground">
        Pausing or removing a customer stops the monthly charge. The month
        already paid for is not refunded.
      </p>
    </div>
  );
}

/** One selectable plan in the Add-customer dialog. */
function PlanChoice({
  plan,
  selected,
  onSelect,
}: {
  plan: CustomerPlan;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        selected
          ? "border-primary bg-primary/5 ring-1 ring-primary"
          : "hover:border-muted-foreground/40",
      )}
    >
      <p className="truncate text-sm font-medium">{plan.name}</p>
      <p className="mt-1 text-lg font-semibold">
        {money(plan.price_cents)}
        <span className="text-xs font-normal text-muted-foreground"> /mo</span>
      </p>
      <p className="mt-0.5 text-xs text-muted-foreground">
        {plan.included_minutes.toLocaleString()} min ·{" "}
        {perMinute(plan.rate_per_minute_cents)}/min after
      </p>
    </button>
  );
}

/** The tenant's cost against their selling price — the reason to pick a plan.
 *
 * Same arithmetic as the plan dialog, from the same helper, so the margin
 * quoted here can never disagree with the one quoted where the plan was built.
 */
function MarginLine({ plan, preview }: { plan: CustomerPlan; preview: BillingPreview }) {
  const e = planEconomics({
    priceCents: plan.price_cents,
    includedMinutes: plan.included_minutes,
    feeCents: preview.fee_cents,
    costPerMinuteCents: preview.per_minute_cents,
  });
  return (
    <div className="space-y-1 rounded-lg border bg-muted/40 p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <span className="text-muted-foreground">
          Costs you <span className="font-medium text-foreground">{money(e.cost)}</span> /mo
        </span>
        <span className="text-muted-foreground">
          They pay you <span className="font-medium text-foreground">{money(e.price)}</span> /mo
        </span>
        <span
          className={cn(
            "font-medium",
            e.profit >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive",
          )}
        >
          {e.profit >= 0 ? "Profit" : "Loss"} {money(Math.abs(e.profit))} /mo
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        {money(preview.fee_cents)} customer fee + {plan.included_minutes.toLocaleString()}{" "}
        minutes × {perMinute(preview.per_minute_cents)}, if they use them all.
      </p>
    </div>
  );
}

function AddCustomerDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [planId, setPlanId] = useState("");

  const qc = useQueryClient();
  const plansQ = useQuery({
    queryKey: ["customer-plans"],
    queryFn: customersApi.plans,
    enabled: open,
  });
  const plans = (plansQ.data ?? []).filter((p) => p.is_active);
  const plan = plans.find((p) => p.id === planId) ?? null;
  // Priced live: the rate lives in the plan, so a stale copy would lie.
  const previewQ = useQuery({
    queryKey: [...KEY, "billing-preview"],
    queryFn: customersApi.billingPreview,
    enabled: open,
    staleTime: 0,
  });
  const preview = previewQ.data;
  const blocked = !!preview && preview.configured && !preview.affordable;

  const create = useMutation({
    mutationFn: () =>
      customersApi.create({ name, email, password, notes, plan_id: planId }),
    onSuccess: () => {
      toast.success(
        preview?.configured
          ? `Customer created — ${money(preview.fee_cents)} charged. Share the login email + password with them.`
          : "Customer created. Share the login email + password with them.",
      );
      setName(""); setEmail(""); setPassword(""); setNotes(""); setPlanId("");
      // The wallet just moved: refresh the balance wherever it is shown.
      qc.invalidateQueries({ queryKey: ["billing"] });
      onCreated();
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add customer</DialogTitle>
          <DialogDescription>
            Creates the customer and their login. They see this same dashboard, with
            only their own rows in it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Plan</Label>
          {plansQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : plans.length === 0 ? (
            <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
              You have no plans yet, and every customer must be on one.{" "}
              <Link
                to="/customer-plans"
                className="font-medium text-foreground underline underline-offset-2"
              >
                Create a plan
              </Link>{" "}
              first.
            </div>
          ) : (
            <>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {plans.map((p) => (
                  <PlanChoice
                    key={p.id}
                    plan={p}
                    selected={p.id === planId}
                    onSelect={() => setPlanId(p.id)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Sets what this customer pays you and how many minutes they get
                each month.{" "}
                <Link to="/customer-plans" className="underline underline-offset-2">
                  Manage plans
                </Link>
              </p>
            </>
          )}
        </div>

        {plan && preview?.configured && (
          <MarginLine plan={plan} preview={preview} />
        )}

        {previewQ.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          preview && <CostPanel p={preview} />
        )}
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cust-name">Customer / company name</Label>
            <Input id="cust-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-email">Login email</Label>
            <Input id="cust-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-pw">Password</Label>
            <Input id="cust-pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cust-notes">Internal notes (optional)</Label>
            <Textarea id="cust-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => create.mutate()}
            disabled={
              !name || !email || !password || !planId || create.isPending || blocked
            }
          >
            {create.isPending
              ? "Creating…"
              : preview?.configured && preview.affordable
                ? `Create customer · ${money(preview.fee_cents)}`
                : "Create customer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
